import { FC, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Switch,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import { OfflineNotice } from '../../offline';
import { useSnackbar } from 'notistack';

interface PaymentType {
  id: string;
  label: string;
  value: string;
  isActive: boolean;
  sortOrder: number;
}

/** PAY-8: manage the clinic's configurable payment types (Settings → Payment Types). */
const PaymentTypesSettings: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');

  const { data: types = [], isLoading } = useQuery<PaymentType[]>(
    ['payment-types', 'all'],
    () =>
      api
        .get('/payment-types', { params: { includeInactive: true } })
        .then((res) => res.data || []),
    {
      onError: () => {
        enqueueSnackbar('Failed to load payment types', { variant: 'error' });
      },
    },
  );

  // Consumers (bulk payment, billing, invoice wizard) cache the active-only list
  // under ['payment-types']; the prefix invalidation reaches them AND this
  // screen's ['payment-types', 'all'] — otherwise edits here go stale elsewhere
  // until a hard refresh.
  const invalidate = () => queryClient.invalidateQueries(['payment-types']);

  const addMutation = useMutation(
    (label: string) => api.post('/payment-types', { label }),
    {
      onSuccess: () => {
        setNewLabel('');
        enqueueSnackbar('Payment type added', { variant: 'success' });
        invalidate();
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message || 'Failed to add payment type', { variant: 'error' });
      },
    },
  );

  const add = () => {
    if (!newLabel.trim()) return;
    addMutation.mutate(newLabel.trim());
  };

  const toggleMutation = useMutation(
    (t: PaymentType) => api.put(`/payment-types/${t.id}`, { isActive: !t.isActive }),
    {
      onSuccess: () => {
        invalidate();
      },
      onError: () => {
        enqueueSnackbar('Failed to update', { variant: 'error' });
      },
    },
  );

  const renameMutation = useMutation(
    ({ id, label }: { id: string; label: string }) => api.put(`/payment-types/${id}`, { label }),
    {
      onSuccess: () => {
        setEditingId(null);
        invalidate();
      },
      onError: () => {
        enqueueSnackbar('Failed to rename', { variant: 'error' });
      },
    },
  );

  const saveEdit = (t: PaymentType) => {
    if (!editLabel.trim()) return;
    renameMutation.mutate({ id: t.id, label: editLabel.trim() });
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <OfflineNotice>
        You're offline — editing payment types needs a connection. Offline payment recording
        falls back to the standard types until you reconnect.
      </OfflineNotice>
      <Typography variant="h6" fontWeight="bold" gutterBottom>Payment Types</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These appear in the payment-method dropdown when recording payments. Deactivating
        one hides it from new payments but keeps it on past records.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            label="New payment type"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            placeholder="e.g. On Trade"
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={add}
            disabled={addMutation.isLoading || !newLabel.trim()}
          >
            Add
          </Button>
        </Stack>
      </Paper>

      {isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Paper variant="outlined">
          <List dense>
            {types.map((t) => (
              <ListItem
                key={t.id}
                divider
                secondaryAction={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {editingId !== t.id && (
                      <IconButton size="small" onClick={() => { setEditingId(t.id); setEditLabel(t.label); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Tooltip title={t.isActive ? 'Active' : 'Inactive'}>
                      <Switch edge="end" checked={t.isActive} onChange={() => toggleMutation.mutate(t)} />
                    </Tooltip>
                  </Stack>
                }
              >
                {editingId === t.id ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, pr: 10 }}>
                    <TextField
                      size="small"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(t); }}
                      autoFocus
                      sx={{ flex: 1 }}
                    />
                    <IconButton size="small" color="primary" onClick={() => saveEdit(t)}><CheckIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => setEditingId(null)}><CloseIcon fontSize="small" /></IconButton>
                  </Stack>
                ) : (
                  <ListItemText primary={t.label} secondary={t.value} sx={{ opacity: t.isActive ? 1 : 0.5 }} />
                )}
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default PaymentTypesSettings;
